import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, HelpCircle, RefreshCw } from "lucide-react";
import { Seo } from "@/components/Seo";
import { HeroSection } from "@/components/HeroSection";
import { CategoryCircles } from "@/components/CategoryCircles";
import { SplitEditorial } from "@/components/SplitEditorial";
import { MoodDiscovery } from "@/components/MoodDiscovery";
import { EditorialBanner } from "@/components/EditorialBanner";
import { FashionProductGrid } from "@/components/FashionProductGrid";
import api from "@/lib/api";

const SECONDARY_COLLECTIONS = [
  {
    title: "THE LINEN EDIT",
    subtitle: "Natural texture. Modern ease.",
    cta: "SHOP LINEN",
    image: "https://images.unsplash.com/photo-1596455607563-ad6193f76b17?w=600&h=800&fit=crop&q=80",
    link: "/search?q=linen",
  },
  {
    title: "AFTER DARK",
    subtitle: "Evening silhouettes with quiet confidence.",
    cta: "EXPLORE EVENING",
    image: "https://images.unsplash.com/photo-1512413914565-eb738f6d7ab7?w=600&h=800&fit=crop&q=80",
    link: "/search?q=evening+wear",
  },
  {
    title: "SIGNATURE ESSENTIALS",
    subtitle: "Pieces worth wearing again.",
    cta: "SHOP ESSENTIALS",
    image: "https://images.unsplash.com/photo-1591561954557-26941169b49e?w=600&h=800&fit=crop&q=80",
    link: "/search?q=essentials",
  },
];

export default function Home() {
  const [newArrivals, setNewArrivals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get("/products", { params: { limit: 4, sort: "newest", category: "women,men" } });
        setNewArrivals(res.data?.products || res.data || []);
      } catch (err) {
        console.error("Failed to load homepage products:", err);
      } finally { setLoading(false); }
    };
    fetchProducts();
  }, []);

  return (
    <>
      <Seo 
        path="/" 
        title="Farniymart | Modern Luxury Fashion for Women & Men"
        description="Discover contemporary women's and men's fashion at Farniymart. Explore refined everyday essentials, new arrivals and seasonal collections."
      />

      {/* ── Hero ── */}
      <HeroSection />

      {/* ── Circular Category Discovery ── */}
      <CategoryCircles />

      {/* ── Split Editorial Collections ── */}
      <SplitEditorial />

      {/* ── Secondary Collection Grid ── */}
      <section className="fashion-section">
        <div className="fashion-container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {SECONDARY_COLLECTIONS.map((col) => (
              <Link
                key={col.title}
                to={col.link}
                className="group flex flex-col items-center text-center"
              >
                <div className="w-full aspect-[3/4] overflow-hidden mb-6">
                  <img
                    src={col.image}
                    alt={col.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-display text-xl md:text-2xl font-light mb-2">{col.title}</h3>
                <p className="text-fashion-text-muted text-sm md:text-base mb-4">{col.subtitle}</p>
                <span className="text-xs tracking-widest uppercase border-b border-fashion-text pb-1 group-hover:text-fashion-text-muted group-hover:border-fashion-text-muted transition-colors">
                  {col.cta}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── New Arrivals ── */}
      <section className="fashion-section bg-fashion-bg-warm">
        <div className="fashion-container">
          <div className="fashion-section-header">
            <p className="text-overline mb-3">Fresh silhouettes for the season ahead.</p>
            <h2 className="font-display text-display-md font-light">NEW ARRIVALS</h2>
          </div>
          <FashionProductGrid products={newArrivals.slice(0, 4)} loading={loading} />
          <div className="text-center mt-12">
            <Link to="/new-arrivals" className="btn-fashion btn-fashion-outline">
              Shop All New Arrivals <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Editorial Story ── */}
      <EditorialBanner
        title="MADE FOR YOUR PACE"
        subtitle=""
        cta="DISCOVER THE COLLECTION"
        link="/collections"
        image="https://images.unsplash.com/photo-1549420078-45ec37d15fc2?w=1400&h=800&fit=crop&q=85"
      />

      {/* ── Shop By Mood ── */}
      <MoodDiscovery />

      {/* ── Trust Pillars ── */}
      <section className="fashion-section border-t border-fashion-border">
        <div className="fashion-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <ShieldCheck className="h-6 w-6 mx-auto mb-3 text-fashion-text-muted" strokeWidth={1} />
              <h4 className="text-xs font-medium tracking-[0.1em] uppercase mb-1.5">Secure Payments</h4>
              <p className="text-xs text-fashion-text-light">Encrypted transactions</p>
            </div>
            <div>
              <RefreshCw className="h-6 w-6 mx-auto mb-3 text-fashion-text-muted" strokeWidth={1} />
              <h4 className="text-xs font-medium tracking-[0.1em] uppercase mb-1.5">Easy Returns</h4>
              <p className="text-xs text-fashion-text-light">15-day return policy</p>
            </div>
            <div>
              <Truck className="h-6 w-6 mx-auto mb-3 text-fashion-text-muted" strokeWidth={1} />
              <h4 className="text-xs font-medium tracking-[0.1em] uppercase mb-1.5">Order Tracking</h4>
              <p className="text-xs text-fashion-text-light">Track via your account</p>
            </div>
            <div>
              <HelpCircle className="h-6 w-6 mx-auto mb-3 text-fashion-text-muted" strokeWidth={1} />
              <h4 className="text-xs font-medium tracking-[0.1em] uppercase mb-1.5">Customer Support</h4>
              <p className="text-xs text-fashion-text-light">Dedicated assistance</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
